import { useState } from 'react';
import { format } from 'date-fns';
import { el } from 'date-fns/locale';
import { CalendarIcon } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { cn } from '@/lib/utils';

interface DateInputProps {
	value: Date | null;
	onChange: (date: Date | null) => void;
	placeholder?: string;
	disabled?: boolean;
	/** date-fns format; defaults to 'dd MMM yyyy' with the Greek locale. */
	displayFormat?: string;
	minDate?: Date;
	maxDate?: Date;
}

/** Readonly text input + calendar icon; opens a shadcn Calendar popover. */
export function DateInput({
	value,
	onChange,
	placeholder = '—',
	disabled,
	displayFormat = 'dd MMM yyyy',
	minDate,
	maxDate
}: DateInputProps) {
	const [open, setOpen] = useState(false);

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild disabled={disabled}>
				<button
					type="button"
					className={cn(
						'flex w-full items-center justify-between rounded-md border border-border bg-surface px-3 py-2 text-sm text-foreground transition-colors',
						'focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50'
					)}>
					<span className={cn(!value && 'text-foreground-subtle')}>
						{value ? format(value, displayFormat, { locale: el }) : placeholder}
					</span>
					<CalendarIcon size={16} className="pointer-events-none text-foreground-muted" />
				</button>
			</PopoverTrigger>
			<PopoverContent className="w-auto p-0" align="start">
				<Calendar
					mode="single"
					selected={value ?? undefined}
					onSelect={date => {
						onChange(date ?? null);
						setOpen(false);
					}}
					disabled={date => Boolean((minDate && date < minDate) || (maxDate && date > maxDate))}
					locale={el}
					autoFocus
				/>
			</PopoverContent>
		</Popover>
	);
}
